import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { notificationsAPI } from '../api/notifications';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';

const PER_PAGE = 10;

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage]                   = useState(1);
  const [lastPage, setLastPage]           = useState(1);
  const [loading, setLoading]             = useState(true);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [error, setError]                 = useState(null);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    try {
      const res  = await notificationsAPI.getList(pageNum, PER_PAGE);
      const body = res.data;
      const items = body.data?.notifications ?? [];
      const meta  = body.data?.pagination   ?? {};

      setNotifications((prev) => append ? [...prev, ...items] : items);
      setLastPage(meta.last_page ?? 1);
      setError(null);
    } catch {
      setError('Failed to load notifications. Please try again.');
    }
  }, []);

  // Initial load
  useEffect(() => {
    setLoading(true);
    fetchPage(1, false).finally(() => setLoading(false));
  }, [fetchPage]);

  const handleLoadMore = async () => {
    const next = page + 1;
    setLoadingMore(true);
    await fetchPage(next, true);
    setPage(next);
    setLoadingMore(false);
  };

  const hasMore = page < lastPage;

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={notifications.length > 0 ? `${notifications.length} notification${notifications.length !== 1 ? 's' : ''}` : 'No notifications yet'}
      />

      <div className="px-4 py-4 pb-24">
        {/* Initial loading skeletons */}
        {loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton h-20 rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <Card className="text-center py-10">
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 text-xs"
              onClick={() => {
                setLoading(true);
                fetchPage(1, false).finally(() => setLoading(false));
              }}
            >
              Retry
            </Button>
          </Card>
        ) : notifications.length === 0 ? (
          <Card className="text-center py-14">
            <div
              className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            >
              <BellOff size={28} color="rgba(255,255,255,0.35)" />
            </div>
            <p className="font-medium text-white">No notifications</p>
            <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
              You're all caught up!
            </p>
          </Card>
        ) : (
          <>
            <AnimatePresence>
              <div className="flex flex-col gap-2">
                {notifications.map((n, idx) => (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(idx, 10) * 0.04 }}
                    className="rounded-2xl p-4 border"
                    style={{
                      background: 'rgba(212,160,23,0.08)',
                      borderColor: 'rgba(212,160,23,0.25)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
                      >
                        <Bell size={16} color="white" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-medium leading-snug"
                          style={{ color: '#ffffff' }}
                        >
                          {n.title}
                        </p>
                        {n.description && (
                          <p
                            className="text-xs mt-1 leading-relaxed"
                            style={{ color: 'rgba(255,255,255,0.6)' }}
                          >
                            {n.description}
                          </p>
                        )}
                        <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>

            {/* Load More */}
            {hasMore && (
              <div className="mt-5 flex justify-center">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  loading={loadingMore}
                  className="text-sm px-6 py-2.5 rounded-xl"
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Loading...
                    </span>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}

            {/* End of list indicator */}
            {!hasMore && notifications.length > PER_PAGE && (
              <p className="text-center text-xs mt-5" style={{ color: 'rgba(255,255,255,0.3)' }}>
                No more notifications
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
